import { SocketIoTransport } from "./SocketIoTransport";
import type { SignalingTransport } from "./SignalingTransport";
import { WebSocketTransport } from "./WebSocketTransport";

export interface SignalingEndpoint {
  transport: "socket-io" | "websocket";
  url: string;
}

export type ConnectionMode = "online" | "lan";

export const createSignalingTransport = (mode: ConnectionMode): SignalingTransport => {
  const endpoint = resolveSignalingEndpoint(
    mode === "lan" ? import.meta.env.VITE_LAN_SIGNALING_URL : import.meta.env.VITE_SIGNALING_URL,
    window.location,
    import.meta.env.DEV,
    mode,
  );
  return endpoint.transport === "websocket"
    ? new WebSocketTransport(endpoint.url)
    : new SocketIoTransport(endpoint.url, mode === "lan");
};

export const resolveSignalingEndpoint = (
  configuredUrl: string | undefined,
  location: Pick<Location, "href" | "hostname" | "host" | "protocol">,
  development: boolean,
  mode: ConnectionMode = "online",
): SignalingEndpoint => {
  const configured = configuredUrl?.trim();
  if (configured) {
    const url = new URL(configured, location.href);
    if (mode === "lan" && location.protocol === "https:" && url.protocol === "http:") {
      throw new Error("LAN signaling cannot use HTTP from the secure online site. Open Mini Doom from the LAN host address instead.");
    }
    if (url.protocol === "ws:" || url.protocol === "wss:") return { transport: "websocket", url: url.href };
    if (url.protocol === "http:" || url.protocol === "https:") return { transport: "socket-io", url: url.origin };
    throw new Error("VITE_SIGNALING_URL must use http, https, ws, or wss.");
  }
  if (mode === "lan") {
    if (!development && !isPrivateHost(location.hostname)) {
      return sameOriginWorkerEndpoint(location);
    }
    return {
      transport: "socket-io",
      url: `${location.protocol}//${location.hostname}:3001`,
    };
  }
  if (development) {
    return {
      transport: "socket-io",
      url: `${location.protocol}//${location.hostname}:3001`,
    };
  }
  return sameOriginWorkerEndpoint(location);
};

const sameOriginWorkerEndpoint = (
  location: Pick<Location, "host" | "protocol">,
): SignalingEndpoint => {
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  return { transport: "websocket", url: `${protocol}//${location.host}/api/signal` };
};

const isPrivateHost = (hostname: string): boolean => {
  const host = hostname.replace(/^\[|\]$/g, "").toLowerCase();
  if (host === "localhost" || host === "::1" || host.endsWith(".local")) return true;
  if (host.includes(":")) return /^(?:f[cd]|fe[89ab])/.test(host);
  const octets = host.split(".").map(Number);
  if (octets.length !== 4 || octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)) return false;
  return octets[0] === 10
    || octets[0] === 127
    || (octets[0] === 169 && octets[1] === 254)
    || (octets[0] === 172 && octets[1]! >= 16 && octets[1]! <= 31)
    || (octets[0] === 192 && octets[1] === 168);
};
